import { alpha, ClickAwayListener, styled } from "@mui/material";
import { GlobalFilter, SearchInput } from "@volt_developers/components";
import debounce from "lodash/debounce";
import { Dispatch, SetStateAction, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";

import { SIDEBAR_WIDTH } from "../../../constants";
import { TEST_IDS } from "../../../constants/TestIds";
import { GlobalFilterSearchList } from "./GlobalFilterSearchList";
import { GlobalFilterSearchNoResults } from "./GlobalFilterSearchNoResults";

import type {
  SearchResultCustomer,
  SearchResults,
  SelectedCustomer,
} from "./useGlobalFilterSearch";

export interface GlobalFilterSearchProps {
  searchResults: SearchResults;
  isLoading: boolean;
  isNoResults: boolean;
  hasMore: boolean;
  onSearch: (search: string) => void;
  onSelectCustomer: (customer: SelectedCustomer) => void;
  onCloseFilterSearch: Dispatch<SetStateAction<boolean>>;
  handleLoadMore: VoidFunction;
}

export function GlobalFilterSearch({
  onCloseFilterSearch,
  searchResults,
  isLoading,
  isNoResults,
  hasMore,
  onSearch,
  onSelectCustomer,
  handleLoadMore,
}: GlobalFilterSearchProps) {
  const { t } = useTranslation("common");
  const handleCloseFilterSearch = useCallback(
    () => onCloseFilterSearch(false),
    [onCloseFilterSearch]
  );
  const searchRef = useRef<HTMLInputElement>(null);

  const handleSelectCustomer = (selectedCustomer: SearchResultCustomer) => {
    onSelectCustomer(selectedCustomer);
    handleCloseFilterSearch();
  };

  const handleSearchChange = debounce((search: string) => {
    onSearch(search);
  }, DEBOUNCE_DELAY);

  const handleClear = () => {
    onSearch("");

    if (searchRef.current) {
      searchRef.current.value = "";
    }
  };

  return (
    <Overlay>
      <ClickAwayListener onClickAway={handleCloseFilterSearch}>
        <Container data-testid={TEST_IDS.globalFilterSearch}>
          <GlobalFilter.Header sx={{ p: 2 }}>
            <SearchInput
              ref={searchRef}
              placeholder={t(COMMON_TRANSLATION_KEYS.searchCustomers)}
              onChange={(e) => handleSearchChange(e.target.value)}
              onClear={handleClear}
            />
          </GlobalFilter.Header>
          {isNoResults ? (
            <GlobalFilterSearchNoResults
              search={searchRef.current?.value ?? ""}
              handleResetSearch={handleClear}
            />
          ) : (
            <GlobalFilterSearchList
              searchResults={searchResults}
              isLoading={isLoading}
              hasMore={hasMore}
              handleSelectCustomer={handleSelectCustomer}
              handleLoadMore={handleLoadMore}
            />
          )}
        </Container>
      </ClickAwayListener>
    </Overlay>
  );
}

const DEBOUNCE_DELAY = 500;
const GLOBAL_FILTER_SEARCH_WIDTH = 315;

const Overlay = styled("div")({
  position: "absolute",
  left: SIDEBAR_WIDTH,
  backgroundColor: alpha("#333333", 0.33),
  height: "100%",
  width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
  zIndex: 2,
  overflow: "hidden",
});

const Container = styled("div")(({ theme }) => ({
  width: GLOBAL_FILTER_SEARCH_WIDTH,
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.paper,
  height: "100%",
}));
